import React from 'react';

/**
 * CustomerModal - Modal for view/add/edit customer
 * Contains: CustomerViewContent, CustomerForm sub-components
 * Handles: view mode (read-only), add mode (new customer), edit mode (update)
 */

// View content sub-component
const CustomerViewContent = ({ customer, isAdmin, onEdit, onDelete }) => {
  return (
    <div className="modal-body">
      <div className="view-grid">
        <div className="view-item">
          <strong>Name:</strong>
          <span>{customer.name}</span>
        </div>
        <div className="view-item">
          <strong>Address:</strong>
          <span>{customer.address}</span>
        </div>
        <div className="view-item">
          <strong>City:</strong>
          <span>{customer.city}</span>
        </div>
        <div className="view-item">
          <strong>State:</strong>
          <span>{customer.state}</span>
        </div>
        <div className="view-item">
          <strong>ZIP:</strong>
          <span>{customer.zip}</span>
        </div>
        <div className="view-item">
          <strong>Country:</strong>
          <span>{customer.country}</span>
        </div>
        {customer.contact_person && (
          <div className="view-item">
            <strong>Contact Person:</strong>
            <span>{customer.contact_person}</span>
          </div>
        )}
        {customer.phone && (
          <div className="view-item">
            <strong>Phone:</strong>
            <span>{customer.phone}</span>
          </div>
        )}
        {customer.email && (
          <div className="view-item">
            <strong>Email:</strong>
            <span>{customer.email}</span>
          </div>
        )}
        {customer.irs_ss_number && (
          <div className="view-item">
            <strong>IRS/SS Number:</strong>
            <span>{customer.irs_ss_number}</span>
          </div>
        )}
        {customer.duns_number && (
          <div className="view-item">
            <strong>DUNS Number:</strong>
            <span>{customer.duns_number}</span>
          </div>
        )}
        {!customer.fsvp_same_as_customer && (
          <>
            <div className="view-section-title">FSVP Importer Information</div>
            <div className="view-item">
              <strong>FSVP Name:</strong>
              <span>{customer.fsvp_name}</span>
            </div>
            <div className="view-item">
              <strong>FSVP Address:</strong>
              <span>{customer.fsvp_address}</span>
            </div>
            <div className="view-item">
              <strong>FSVP City:</strong>
              <span>{customer.fsvp_city}</span>
            </div>
            <div className="view-item">
              <strong>FSVP State:</strong>
              <span>{customer.fsvp_state}</span>
            </div>
            <div className="view-item">
              <strong>FSVP ZIP:</strong>
              <span>{customer.fsvp_zip}</span>
            </div>
            <div className="view-item">
              <strong>FSVP Email:</strong>
              <span>{customer.fsvp_email}</span>
            </div>
            <div className="view-item">
              <strong>FSVP DUNS:</strong>
              <span>{customer.fsvp_duns_number}</span>
            </div>
          </>
        )}
        {customer.notes && (
          <div className="view-item full-width">
            <strong>Notes:</strong>
            <span>{customer.notes}</span>
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="modal-footer">
          <button className="btn-danger" onClick={onDelete}>
            Delete
          </button>
          <button className="btn-primary" onClick={onEdit}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

// Form sub-component for add/edit
const CustomerForm = ({ formData, onInputChange, onSubmit, onCancel, mode }) => {
  return (
    <form onSubmit={onSubmit} className="modal-body">
      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>Customer Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="form-group full-width">
            <label>Address *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>State *</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>ZIP *</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={onInputChange}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={onInputChange}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={onInputChange}
            />
          </div>
          <div className="form-group">
            <label>Contact Person</label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={onInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Customs Clearance</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>IRS/SS Number</label>
            <input
              type="text"
              name="irs_ss_number"
              value={formData.irs_ss_number}
              onChange={onInputChange}
            />
          </div>
          <div className="form-group">
            <label>DUNS Number</label>
            <input
              type="text"
              name="duns_number"
              value={formData.duns_number}
              onChange={onInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>FSVP Importer (FDA)</h3>
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="fsvp_same_as_customer"
              checked={formData.fsvp_same_as_customer}
              onChange={onInputChange}
            />
            Same as Customer
          </label>
        </div>

        {!formData.fsvp_same_as_customer && (
          <div className="form-grid">
            <div className="form-group full-width">
              <label>FSVP Name</label>
              <input
                type="text"
                name="fsvp_name"
                value={formData.fsvp_name}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group full-width">
              <label>FSVP Address</label>
              <input
                type="text"
                name="fsvp_address"
                value={formData.fsvp_address}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group">
              <label>FSVP City</label>
              <input
                type="text"
                name="fsvp_city"
                value={formData.fsvp_city}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group">
              <label>FSVP State</label>
              <input
                type="text"
                name="fsvp_state"
                value={formData.fsvp_state}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group">
              <label>FSVP ZIP</label>
              <input
                type="text"
                name="fsvp_zip"
                value={formData.fsvp_zip}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group">
              <label>FSVP Email</label>
              <input
                type="email"
                name="fsvp_email"
                value={formData.fsvp_email}
                onChange={onInputChange}
              />
            </div>
            <div className="form-group">
              <label>FSVP DUNS Number</label>
              <input
                type="text"
                name="fsvp_duns_number"
                value={formData.fsvp_duns_number}
                onChange={onInputChange}
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Additional Information</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={onInputChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={onInputChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          {mode === 'add' ? 'Add Customer' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

// Main modal component
const CustomerModal = ({
  show,
  mode,
  customer,
  formData,
  isAdmin,
  onClose,
  onSubmit,
  onInputChange,
  onEdit,
  onDelete
}) => {
  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {mode === 'view' && 'Customer Details'}
            {mode === 'add' && 'Add New Customer'}
            {mode === 'edit' && 'Edit Customer'}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {mode === 'view' ? (
          <CustomerViewContent
            customer={customer}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ) : (
          <CustomerForm
            formData={formData}
            onInputChange={onInputChange}
            onSubmit={onSubmit}
            onCancel={onClose}
            mode={mode}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerModal;
